import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  CommonSummaryStatusState,
  DocumentAiSummariesState,
  OrganizeStatusState,
} from '@/recoil/atom/documents';
import { MyInfoState } from '@/recoil/atom';
import { getLexicalNodesFromMarkdown } from '@/components/Document/@hooks/useAiSummaries';
import { useSSE } from '@/hooks/sse/useSSE';
import { useAiSummeryResult, useAiSummeryStatus } from '@/hooks/apiHooks';
import { useSWRConfig } from 'swr';
import { apiURL } from '@/constant/apiURL';
import { isPendingAiSummary, removePendingAiSummary } from '@/utils/aiAutoFill';

export default function useBatchPollingAiSummaries({
  docId,
  onCompleted,
  onError,
}: {
  docId: string;
  onCompleted?: (dom: string) => void;
  onError?: () => void;
}) {
  const updateAiSummaries = useSetAtom(DocumentAiSummariesState);
  const aiSummaries = useAtomValue(DocumentAiSummariesState);
  const setSummaryStatus = useSetAtom(CommonSummaryStatusState);
  const setOrganizeStatus = useSetAtom(OrganizeStatusState);
  const { passportId } = useAtomValue(MyInfoState);
  const { mutate } = useSWRConfig();

  const topics = useMemo(() => (docId ? [`talk-flow:${docId}`] : []), [docId]);

  // 임시 ID -> 원래 ID 매핑 저장
  const pipelineIdMap = useMemo(() => new Map<string, string>(), []);

  // 자동 채우기를 이미 실행한 항목 추적
  const autoFilledRef = useRef<Set<string>>(new Set());

  // docId가 변경되면 autoFilledRef 초기화
  useEffect(() => {
    autoFilledRef.current = new Set();
  }, [docId]);

  const handleUpdate = useCallback(
    async (serverStatus: string, pipelineId?: string) => {
      if (!pipelineId) return;

      let text = '';
      let dom = '';

      // 완료 상태일 때만 결과 조회
      if (serverStatus === 'done' && pipelineId) {
        try {
          const res = await useAiSummeryResult(pipelineId);
          const rawText = res?.result?.summarize_text ?? '';

          if (rawText) {
            text = rawText;
            dom = await getLexicalNodesFromMarkdown(text);
          }
        } catch (e) {
          serverStatus = 'error_done';
        }
      }

      updateAiSummaries((prev) => {
        if (!prev[pipelineId]) {
          return prev;
        }
        if (serverStatus === 'done' && prev[pipelineId].content) {
          return prev;
        }

        // 서버 상태를 UI 상태로 변환
        let uiState: any = prev[pipelineId].uiState; // 기본값은 현재 상태 유지
        switch (serverStatus) {
          case 'done':
            uiState = 'completed';
            break;
          case 'failover_done':
          case 'validation_error_done':
            uiState = 'validation_error';
            break;
          case 'error_done':
            uiState = 'error';
            break;
          default:
            uiState = 'loading';
            break;
        }

        const next = {
          ...prev,
          [pipelineId]: {
            ...prev[pipelineId],
            uiState: uiState,
            content: text || prev[pipelineId].content,
            contentDom: dom || prev[pipelineId].contentDom,
          },
        };

        // 2. 사용자가 요청한 AI 요약만 자동 채우기
        if (serverStatus === 'done' && dom && onCompleted) {
          // 사용자가 요청한 항목인지 확인
          if (!isPendingAiSummary(docId, pipelineId)) {
            // 요청하지 않은 요약은 자동 채우기 안 함
            return next;
          }

          // 사용자가 요청한 항목들 중 content가 비어있는 것들만 필터링
          const pendingItems = Object.entries(prev).filter(
            ([id, item]) =>
              isPendingAiSummary(docId, id) &&
              (!item.content || item.content === ''),
          );

          // 막 완료된 항목이 pendingItems에 없으면 추가
          if (!pendingItems.find(([id]) => id === pipelineId)) {
            pendingItems.push([pipelineId, prev[pipelineId]]);
          }

          let earliestId = '';
          let earliestTime = Infinity;

          pendingItems.forEach(([itemId, item]) => {
            const time = item.createdAt?.valueOf() ?? Infinity;
            if (time < earliestTime) {
              earliestTime = time;
              earliestId = itemId;
            }
          });

          // 가장 먼저 생성된 항목이 막 완료된 항목이면 onCompleted 호출
          if (pipelineId === earliestId) {
            onCompleted(dom);
            // 자동 채우기 후 localStorage에서 제거
            removePendingAiSummary(docId, pipelineId);
            // 자동 채우기를 실행했음을 기록
            autoFilledRef.current.add(pipelineId);
          }
        }

        return next;
      });
    },
    [updateAiSummaries, onCompleted],
  );

  // 초기 마운트 시 또는 aiSummaries 변경 시 pending 상태 확인하여 자동 채우기
  useEffect(() => {
    const checkPendingCompletedSummaries = () => {
      // pending 상태인 항목들 필터링 (이미 자동 채우기를 한 항목 제외)
      const pendingItems = Object.entries(aiSummaries).filter(
        ([id, item]) =>
          isPendingAiSummary(docId, id) && !autoFilledRef.current.has(id),
      );

      if (pendingItems.length === 0) return;

      // content가 있는 항목만 필터링
      const completedPendingItems = pendingItems.filter(
        ([id, item]) => item.content && item.content.trim() !== '',
      );

      if (completedPendingItems.length === 0) return;

      // 가장 먼저 생성된 항목 찾기
      let earliestId = '';
      let earliestTime = Infinity;

      completedPendingItems.forEach(([itemId, item]) => {
        const time = item.createdAt?.valueOf() ?? Infinity;
        if (time < earliestTime) {
          earliestTime = time;
          earliestId = itemId;
        }
      });

      // 가장 먼저 생성된 항목이 완료되어 있으면 자동 채우기
      if (earliestId && onCompleted) {
        const item = aiSummaries[earliestId];
        if (item.contentDom) {
          onCompleted(item.contentDom);
          removePendingAiSummary(docId, earliestId);
          autoFilledRef.current.add(earliestId);
        }
      }
    };

    checkPendingCompletedSummaries();
  }, [aiSummaries, docId, onCompleted]);

  const handlers = useMemo(() => {
    const isMyEvent = (data: any) => data.payload?.triggerBy === passportId;

    // AI 요약 블록(commonSummary) 전용 상태 — 최초 생성/재생성 공통.
    const setSummaryLoading = () =>
      setSummaryStatus((prev) => ({
        ...prev,
        [docId]: { status: 'loading', startedAt: Date.now() },
      }));

    const clearSummaryStatus = () =>
      setSummaryStatus((prev) => {
        if (!prev[docId]) return prev;
        const next = { ...prev };
        delete next[docId];
        return next;
      });

    // 중복 수신되므로 이미 error 면 그대로 둔다(불필요한 리렌더 방지)
    const setSummaryError = () =>
      setSummaryStatus((prev) =>
        prev[docId]?.status === 'error'
          ? prev
          : {
              ...prev,
              [docId]: {
                status: 'error',
                startedAt: prev[docId]?.startedAt ?? Date.now(),
              },
            },
      );

    // AI 정리(organize) 전용 상태 — AI 요약(setSummary*)과 분리한다.
    const clearOrganizeStatus = () =>
      setOrganizeStatus((prev) => {
        if (!prev[docId]) return prev;
        const next = { ...prev };
        delete next[docId];
        return next;
      });

    const setOrganizeError = () =>
      setOrganizeStatus((prev) =>
        prev[docId]?.status === 'error'
          ? prev
          : {
              ...prev,
              [docId]: {
                status: 'error',
                startedAt: prev[docId]?.startedAt ?? Date.now(),
              },
            },
      );

    const handleStarted = (data: any) => {
      if (!isMyEvent(data)) return;
      // 정리 시작 → 본문 organizing 상태로. 재시작이면 이전 에러를 걷는다.
      setOrganizeStatus((prev) => ({
        ...prev,
        [docId]: { status: 'loading', startedAt: Date.now() },
      }));
      // 서버에서 최신 리스트 가져오기 (새 항목이 추가됨)
      mutate(apiURL.AI_PIPELINE_RESULT_DETAIL(docId, passportId));
    };

    const handleCompleted = async (data: any) => {
      if (!isMyEvent(data)) return;
      const id = data.topic?.split(':')[1];
      let pipelineId = data.payload?.aiPipelineId;

      // 임시 ID인 경우 원래 ID로 변환
      const originalId = pipelineIdMap.get(pipelineId);
      if (originalId) {
        pipelineId = originalId;
      }

      if (id && pipelineId) {
        await handleUpdate('done', pipelineId);
      }
      // 정리 완료 → organizing 상태 해제. commonSummary(AI 요약) 갱신은
      // 상위 onCompleted 의 문서 재조회가 담당한다.
      clearOrganizeStatus();
      // 서버 리스트의 최신 status(running→success) 를 즉시 반영해
      // useLatestAiPipelineStatus 기반 스켈레톤/진행표시가 남지 않도록 재검증한다.
      mutate(apiURL.AI_PIPELINE_RESULT_DETAIL(docId, passportId));
    };

    // 공통 요약본(commonSummary) 상태 전용 핸들러. organize 와 분리된 전용 이벤트로,
    // start/complete/error 가 그대로 AI 요약 블록의 상태가 된다.
    // 최초 생성(common-summary.{start,complete,error})과 재생성(common-summary.regenerate.*)
    // 양쪽 이벤트가 동일한 상태를 구동하므로 같은 함수를 두 이름에 매핑한다. (PORTAL-12298)
    // 주의: 이 이벤트들의 triggerBy 는 null(콜백=API Key 인증)일 수 있어 isMyEvent 필터를 쓰지 않는다.
    // 토픽이 문서 단위(talk-flow:{docId})라 이 커넥션의 이벤트는 모두 해당 문서 것이다.
    const handleCommonSummaryStarted = () => setSummaryLoading();

    // 완료 → 상태를 지우고 문서를 재조회한다. 갱신된 commonSummary.text 가 그대로 표시된다.
    const handleCommonSummaryCompleted = () => {
      clearSummaryStatus();
      mutate(apiURL.TALK_FLOW_DOCUMENT_BY_ID(docId));
    };

    const handleAutoSubjectCompleted = () => {
      mutate(apiURL.TALK_FLOW_DOCUMENT_BY_ID(docId));
    };

    const handleCommonSummaryError = () => setSummaryError();

    const handleError = async (data: any) => {
      if (!isMyEvent(data)) return;
      const id = data.topic?.split(':')[1];
      let pipelineId = data.payload?.aiPipelineId;

      if (!id || !pipelineId) return;

      // 정리 실패 → 본문 영역 전용 에러 배너로만 표시한다(AI 요약 블록은 건드리지 않는다).
      setOrganizeError();

      // 임시 ID인 경우 원래 ID로 변환
      const originalId = pipelineIdMap.get(pipelineId);
      if (originalId) {
        pipelineId = originalId;
      }

      // 실제 status 확인을 위해 API 호출
      try {
        const res = await useAiSummeryStatus(pipelineId);
        const actualStatus = res?.status || 'error';
        handleUpdate(actualStatus, pipelineId);
      } catch (e) {
        handleUpdate('error', pipelineId);
      }

      // 정리 실패 알림 (자동정리 "정리 중" UI 종료 등)
      onError?.();
    };

    const handlePipelineIdChanged = (data: any) => {
      if (!isMyEvent(data)) return;
      const prevAiPipelineId = data.payload?.prevAiPipelineId;
      const aiPipelineId = data.payload?.aiPipelineId;

      if (!prevAiPipelineId || !aiPipelineId) return;

      // 새 ID를 임시 ID로 저장 (나중에 이벤트가 오면 원래 ID로 조회)
      pipelineIdMap.set(aiPipelineId, prevAiPipelineId);
    };

    return {
      'talk-flow.organize.start': handleStarted,
      'talk-flow.organize.complete': handleCompleted,
      'talk-flow.organize.error': handleError,
      'talk-flow.organize.ai-pipeline-id-changed': handlePipelineIdChanged,
      // 최초 생성
      'talk-flow.common-summary.start': handleCommonSummaryStarted,
      'talk-flow.common-summary.complete': handleCommonSummaryCompleted,
      'talk-flow.common-summary.error': handleCommonSummaryError,
      // 재생성 (동작 동일)
      'talk-flow.common-summary.regenerate.start': handleCommonSummaryStarted,
      'talk-flow.common-summary.regenerate.complete':
        handleCommonSummaryCompleted,
      'talk-flow.common-summary.regenerate.error': handleCommonSummaryError,
      'talk-flow.auto-subject.complete': handleAutoSubjectCompleted,
    };
  }, [
    handleUpdate,
    passportId,
    mutate,
    docId,
    onError,
    setSummaryStatus,
    setOrganizeStatus,
  ]);

  useSSE(topics, handlers);
}
